import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDataManagement } from './useDataManagement';
import { useApiIntegration } from './useApiIntegration';
import { useChatManagement } from './useChatManagement';
import { useUIControls } from './useUIControls';
import { useTutorial } from './useTutorial';
import { useTimeManagement } from './useTimeManagement';
import { sortVesselData, VESSEL_DISPLAY_NONE } from '../utils/vesselUtils';
import {
  sortRowsByTimeAsc,
  sortedExerciseStoryRowsFromRaw,
  frameIndexForStoryTime,
  VESSEL_MAP_PLAYBACK_VIEW_MODES
} from '../utils/storyTimeUtils';
import { scenarioCommsStepFromNormalizedTime } from '../data/scenarioCommsData';
import { normalizeDepthSelection } from '../utils/chatPlatformActions';

export const useOceanData = () => {
  const uiControls = useUIControls();
  const [currentsVectorScale, setCurrentsVectorScale] = useState(0.009);
  const [currentsColorBy, setCurrentsColorBy] = useState('speed');
  const [oceanBaseOpacity, setOceanBaseOpacity] = useState(1.0);
  /** Mirrored from MapContainer — same object as map comms overlay (stream/hold/out) for sidebar sync */
  const [scenarioCommsBurst, setScenarioCommsBurst] = useState(null);
  /** Bumps on timeline reset so ExerciseCommsPanel + MapContainer clear comms UI/state. */
  const [playbackResetSerial, setPlaybackResetSerial] = useState(0);
  const notifyPlaybackReset = useCallback(() => {
    setPlaybackResetSerial((n) => n + 1);
  }, []);

  const timeManagement = useTimeManagement();

  const dataManagement = useDataManagement(
    uiControls.selectedArea,
    uiControls.selectedModel,
    null,
    null,
    uiControls.selectedDepth,
    null,
    timeManagement.startDate,
    timeManagement.endDate
  );

  useEffect(() => {
    if (dataManagement.availableModels.length > 0) {
      if (!dataManagement.availableModels.includes(uiControls.selectedModel)) {
        uiControls.setSelectedModel(dataManagement.availableModels[0]);
      }
    }
  }, [dataManagement.availableModels, uiControls]);

  useEffect(() => {
    if (dataManagement.availableDepths.length > 0) {
      const isInvalid = uiControls.selectedDepth === null || !dataManagement.availableDepths.includes(uiControls.selectedDepth);

      if (isInvalid) {
        console.warn("SELECTION IS INVALID. Resetting depth to:", dataManagement.availableDepths[0]);
        uiControls.setSelectedDepth(dataManagement.availableDepths[0]);
      }
    }
    // Note: It's normal for BlueTide datasources to not have depth data - this is not an error
  }, [dataManagement.availableDepths, uiControls]);

  // Ref so this effect doesn't depend on timeManagement (processRawData updates timeManagement state → new ref → loop).
  const processRawDataRef = useRef(timeManagement.processRawData);
  processRawDataRef.current = timeManagement.processRawData;
  useEffect(() => {
    if (dataManagement.rawData) {
      processRawDataRef.current(dataManagement.rawData);
    }
  }, [dataManagement.rawData]);

  // Sync date range only on initial load or when datasource changes — never after user clicks Apply.
  const lastSyncedAreaRef = useRef(null);
  useEffect(() => {
    lastSyncedAreaRef.current = null;
  }, [uiControls.selectedArea]);

  useEffect(() => {
    if (!dataManagement.availableDates?.length) return;
    const area = uiControls.selectedArea;
    if (lastSyncedAreaRef.current === area) return;
    lastSyncedAreaRef.current = area;
    const startDate = new Date(dataManagement.availableDates[0]);
    const endDate = new Date(dataManagement.availableDates[dataManagement.availableDates.length - 1]);
    endDate.setHours(23, 59, 59, 999);
    timeManagement.handleDateRangeChange({ startDate, endDate });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- timeManagement is stable
  }, [dataManagement.availableDates, uiControls.selectedArea]);

  const currentsGeoJSON = useMemo(() => {
    if (!dataManagement.rawData || dataManagement.rawData.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }

    const features = dataManagement.rawData.map(row => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(row.lon), parseFloat(row.lat)],
      },
      properties: {
        direction: parseFloat(row.direction),
        speed: parseFloat(row.nspeed),
      },
    })).filter(feature =>
      !isNaN(feature.geometry.coordinates[0]) &&
      !isNaN(feature.geometry.coordinates[1]) &&
      !isNaN(feature.properties.direction) &&
      !isNaN(feature.properties.speed)
    );

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [dataManagement.rawData]);

  // Vessel playback: same data source as MapContainer (selected vessel or all)
  const vesselDataSource = useMemo(() => {
    if (uiControls.selectedVesselId === VESSEL_DISPLAY_NONE) return [];
    if (uiControls.selectedVesselId && dataManagement.vesselsById?.has(uiControls.selectedVesselId)) {
      return dataManagement.vesselsById.get(uiControls.selectedVesselId) || [];
    }
    return dataManagement.rawData || [];
  }, [dataManagement.rawData, dataManagement.vesselsById, uiControls.selectedVesselId]);

  const storySeries = useMemo(() => {
    if (VESSEL_MAP_PLAYBACK_VIEW_MODES.has(uiControls.viewMode)) {
      const fromSelection = sortVesselData(vesselDataSource);
      if (fromSelection.length > 0) return fromSelection;
      // Exercise overlay can show API tracks while "Vessels to display" is None — still need a story clock.
      if (uiControls.mapLayerVisibility?.exerciseScenario && (dataManagement.rawData?.length ?? 0) > 0) {
        return sortedExerciseStoryRowsFromRaw(dataManagement.rawData);
      }
      return [];
    }
    if (dataManagement.timeSeriesData.length > 0) {
      return dataManagement.timeSeriesData;
    }
    return sortRowsByTimeAsc(dataManagement.rawData || []);
  }, [
    uiControls.viewMode,
    vesselDataSource,
    dataManagement.timeSeriesData,
    dataManagement.rawData,
    uiControls.mapLayerVisibility?.exerciseScenario
  ]);

  const totalFrames = storySeries.length;

  const vesselTimeRange = useMemo(() => {
    const rangeFromRows = (rows) => {
      if (!rows || rows.length === 0) return null;
      const sorted = sortVesselData(rows);
      if (sorted.length === 0) return null;
      const minMs = new Date(sorted[0].timestamp || sorted[0].time).getTime();
      const maxMs = new Date(sorted[sorted.length - 1].timestamp || sorted[sorted.length - 1].time).getTime();
      if (Number.isNaN(minMs) || Number.isNaN(maxMs)) return null;
      return { minMs, maxMs };
    };
    const fromSelection = rangeFromRows(vesselDataSource);
    if (fromSelection) return fromSelection;
    if (uiControls.mapLayerVisibility?.exerciseScenario && (dataManagement.rawData?.length ?? 0) > 0) {
      const sorted = sortedExerciseStoryRowsFromRaw(dataManagement.rawData);
      if (sorted.length === 0) return null;
      const minMs = new Date(sorted[0].timestamp || sorted[0].time).getTime();
      const maxMs = new Date(sorted[sorted.length - 1].timestamp || sorted[sorted.length - 1].time).getTime();
      if (Number.isNaN(minMs) || Number.isNaN(maxMs)) return null;
      return { minMs, maxMs };
    }
    return null;
  }, [vesselDataSource, uiControls.mapLayerVisibility?.exerciseScenario, dataManagement.rawData]);

  const [vesselPlaybackTime, setVesselPlaybackTime] = useState(null);

  // Depend on numeric range only — `vesselTimeRange` is a new object whenever `rawData`
  // identity changes; resetting to minMs on every reference churn fights MapContainer play
  // and prevents scenario comms steps from advancing during animation.
  useEffect(() => {
    if (!vesselTimeRange) {
      setVesselPlaybackTime(null);
      return;
    }
    const { minMs, maxMs } = vesselTimeRange;
    setVesselPlaybackTime((prev) => {
      if (prev == null || Number.isNaN(prev)) return minMs;
      if (prev < minMs || prev > maxMs) return minMs;
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- minMs/maxMs only; full `vesselTimeRange` object identity churns (see comment above effect).
  }, [vesselTimeRange?.minMs, vesselTimeRange?.maxMs]);

  /**
   * Comms beats follow the same story progress as Frame N / total (scrubber), not raw calendar
   * span — uneven AIS timestamps were leaving u stuck in "quiet" while the map showed mid-scenario.
   */
  const scenarioCommsPlaybackStep = useMemo(() => {
    if (!uiControls.mapLayerVisibility?.exerciseScenario) return 0;
    if (vesselPlaybackTime == null) return 0;

    if (VESSEL_MAP_PLAYBACK_VIEW_MODES.has(uiControls.viewMode) && storySeries.length > 0) {
      const idx = frameIndexForStoryTime(vesselPlaybackTime, storySeries);
      const n = storySeries.length;
      const u = n <= 1 ? 0 : idx / Math.max(1, n - 1);
      return scenarioCommsStepFromNormalizedTime(u);
    }

    if (!vesselTimeRange) return 0;
    const { minMs, maxMs } = vesselTimeRange;
    const span = Math.max(1, maxMs - minMs);
    const u = (vesselPlaybackTime - minMs) / span;
    return scenarioCommsStepFromNormalizedTime(u);
  }, [
    uiControls.mapLayerVisibility?.exerciseScenario,
    uiControls.viewMode,
    storySeries,
    vesselTimeRange,
    vesselPlaybackTime
  ]);

  const apiIntegration = useApiIntegration();
  const chatManagement = useChatManagement();
  const tutorial = useTutorial();

  // Ref so Chat API gets the applied date range on first call before next render (avoids stale context)
  const appliedDateRangeRef = useRef(null);

  const onDateRangeChange = useCallback(({ startDate, endDate }) => {
    if (startDate && endDate) {
      appliedDateRangeRef.current = { startDate, endDate };
    }
    timeManagement.handleDateRangeChange({ startDate, endDate });
    if (startDate && endDate) {
      dataManagement.refreshData({ startDate, endDate });
    }
  }, [timeManagement, dataManagement]);

  const fetchData = useCallback((settings) => {
    if (!settings) return;

    if (settings.area && settings.area !== uiControls.selectedArea) {
      uiControls.setSelectedArea(settings.area);
    }
    if (settings.model && settings.model !== uiControls.selectedModel) {
      uiControls.setSelectedModel(settings.model);
    }
    if (settings.depth !== undefined && settings.depth !== null) {
      const normalizedDepth = normalizeDepthSelection(
        settings.depth,
        dataManagement.availableDepths
      );
      if (
        normalizedDepth !== null
        && Number(normalizedDepth) !== Number(uiControls.selectedDepth)
      ) {
        uiControls.setSelectedDepth(normalizedDepth);
      }
    }
    if (settings.startDate && settings.endDate && (settings.startDate !== timeManagement.startDate || settings.endDate !== timeManagement.endDate)) {
      onDateRangeChange({ startDate: settings.startDate, endDate: settings.endDate });
    }
  }, [
    uiControls,
    timeManagement,
    onDateRangeChange,
    dataManagement.availableDepths
  ]);

  // Clear ref once state has caught up so we use time management state thereafter
  useEffect(() => {
    appliedDateRangeRef.current = null;
  }, [timeManagement.startDate, timeManagement.endDate]);

  // Chat API should call this when building context so first message after Apply uses the new range
  const getDateRangeForApi = useCallback(() => {
    if (appliedDateRangeRef.current) {
      return {
        startDate: appliedDateRangeRef.current.startDate,
        endDate: appliedDateRangeRef.current.endDate
      };
    }
    return {
      startDate: timeManagement.startDate,
      endDate: timeManagement.endDate
    };
  }, [timeManagement.startDate, timeManagement.endDate]);

  const handleCurrentsScaleChange = (newScale) => {
    setCurrentsVectorScale(newScale);
  };

  const handleCurrentsColorChange = (newColorBy) => {
    setCurrentsColorBy(newColorBy);
  };



  const handleOceanBaseOpacityChange = (newOpacity) => {
    setOceanBaseOpacity(newOpacity);
  };

  const handlePointAnalysis = (point) => {
    const pointData = dataManagement.rawData.filter(row => {
      if (!row.lat || !row.lon) return false;
      const latDiff = Math.abs(row.lat - point.latitude);
      const lngDiff = Math.abs(row.lon - point.longitude);
      return latDiff < 0.001 && lngDiff < 0.001;
    });

    const latest = pointData[pointData.length - 1];
    const analysisContent = `Point Analysis: ${pointData.length} measurements found at [${point.latitude}, ${point.longitude}]. ` +
      (latest ? `Latest temp: ${latest.temp || 'N/A'}Ã‚Â°C, current speed: ${latest.nspeed || 'N/A'} m/s` : 'No recent measurements.');

    chatManagement.addAIResponse(analysisContent, 'system');
  };

  return {
    isLoading: dataManagement.isLoading,
    hasError: dataManagement.hasError,
    errorMessage: dataManagement.errorMessage,
    dataLoaded: dataManagement.dataLoaded,
    dataSource: dataManagement.dataSource,
    dataQuality: dataManagement.dataQuality,
    connectionStatus: apiIntegration.connectionStatus,
    connectionDetails: apiIntegration.connectionDetails,

    // Raw data
    rawData: dataManagement.rawData,
    vesselsById: dataManagement.vesselsById,
    data: dataManagement.data,
    timeSeriesData: dataManagement.timeSeriesData,
    storySeries,
    totalFrames,
    currentsGeoJSON,

    // UI state
    selectedArea: uiControls.selectedArea,
    selectedModel: uiControls.selectedModel,
    selectedDepth: uiControls.selectedDepth,
    selectedParameter: uiControls.selectedParameter,
    viewMode: uiControls.viewMode, // New view mode
    vesselMarkerDisplay: uiControls.vesselMarkerDisplay,
    setVesselMarkerDisplay: uiControls.setVesselMarkerDisplay,
    setSelectedArea: uiControls.setSelectedArea,
    setSelectedModel: uiControls.setSelectedModel,
    setSelectedDepth: uiControls.setSelectedDepth,
    setSelectedParameter: uiControls.setSelectedParameter,
    setViewMode: uiControls.setViewMode, // New view mode setter
    selectedVesselId: uiControls.selectedVesselId,
    setSelectedVesselId: uiControls.setSelectedVesselId,
    threatLevelFilter: uiControls.threatLevelFilter,
    setThreatLevelFilter: uiControls.setThreatLevelFilter,
    vesselTypeFilter: uiControls.vesselTypeFilter,
    setVesselTypeFilter: uiControls.setVesselTypeFilter,
    vesselTimeRange,
    vesselPlaybackTime,
    setVesselPlaybackTime,
    scenarioCommsPlaybackStep,
    scenarioCommsBurst,
    setScenarioCommsBurst,
    playbackResetSerial,
    notifyPlaybackReset,
    availableAreas: uiControls.availableAreas,
    availableParameters: uiControls.availableParameters,
    availableModels: dataManagement.availableModels,
    availableDepths: dataManagement.availableDepths,
    availableDatasources: dataManagement.availableDatasources,
    dataBounds: dataManagement.dataBounds,
    hasDepthData: dataManagement.hasDepthData,
    hasModelData: dataManagement.hasModelData,

    // Layer visibility
    mapLayerVisibility: uiControls.mapLayerVisibility,
    isSstHeatmapVisible: uiControls.isSstHeatmapVisible,
    heatmapScale: uiControls.heatmapScale,

    // Layer animation controls (for particle/vector layers)
    layerAnimationEnabled: uiControls.layerAnimationEnabled,

    // Wind particle controls
    windVelocityParticleCount: uiControls.windVelocityParticleCount,
    windVelocityParticleOpacity: uiControls.windVelocityParticleOpacity,
    windVelocityParticleSpeed: uiControls.windVelocityParticleSpeed,

    // Custom layer controls
    oceanBaseOpacity,
    currentsVectorScale,
    currentsColorBy,

    // Animation — filled in by OceanDataProvider (single useAnimationControl + sync with vesselPlaybackTime)

    // Time management
    startDate: timeManagement.startDate,
    endDate: timeManagement.endDate,
    currentDate: timeManagement.currentDate,
    timeZone: timeManagement.timeZone,
    setCurrentDate: timeManagement.setCurrentDate,
    setCurrentTime: timeManagement.setCurrentTime,
    setTimeZone: timeManagement.setTimeZone,
    availableDates: dataManagement.availableDates, // Use availableDates from dataManagement, not timeManagement
    availableTimes: timeManagement.availableTimes,
    onDateRangeChange,
    getDateRangeForApi,

    // Environmental data — filled in by OceanDataProvider

    // Chat
    chatMessages: chatManagement.chatMessages,
    isTyping: chatManagement.isTyping,
    typingMessage: chatManagement.typingMessage,
    setIsTyping: chatManagement.startTyping,
    addChatMessage: chatManagement.addChatMessage,
    addUserMessage: chatManagement.addUserMessage,
    addAIResponse: chatManagement.addAIResponse,
    addStreamingAIResponse: chatManagement.addStreamingAIResponse,
    appendToMessage: chatManagement.appendToMessage,
    finalizeStreamingMessage: chatManagement.finalizeStreamingMessage,
    updateMessage: chatManagement.updateMessage,
    removeMessage: chatManagement.removeMessage,
    getThreadId: chatManagement.getThreadId,
    setThreadId: chatManagement.setThreadId,
    startTyping: chatManagement.startTyping,
    stopTyping: chatManagement.stopTyping,
    clearChatMessages: chatManagement.clearChatMessages,

    // Tutorial
    showTutorial: tutorial.showTutorial,
    tutorialStep: tutorial.tutorialStep,
    tutorialMode: tutorial.tutorialMode,
    isFirstTimeUser: tutorial.isFirstTimeUser,
    handleTutorialToggle: tutorial.handleTutorialToggle,
    handleTutorialComplete: tutorial.handleTutorialComplete,
    handleTutorialStepChange: tutorial.goToStep,

    // Actions
    fetchData,
    handlePointAnalysis,
    refreshData: dataManagement.refreshData,
    // handleFrameChange, handleDateTimeChange, envData — provided by OceanDataProvider

    // Layer actions
    toggleMapLayer: uiControls.toggleMapLayer,
    toggleSstHeatmap: uiControls.toggleSstHeatmap,
    onHeatmapScaleChange: uiControls.setHeatmapScale,
    toggleLayerAnimation: uiControls.toggleLayerAnimation,

    // Wind particle actions
    onWindVelocityParticleCountChange: uiControls.setWindVelocityParticleCount,
    onWindVelocityParticleOpacityChange: uiControls.setWindVelocityParticleOpacity,
    onWindVelocityParticleSpeedChange: uiControls.setWindVelocityParticleSpeed,

    // Vector/custom layer config
    handleCurrentsScaleChange,
    handleCurrentsColorChange,

    handleOceanBaseOpacityChange
  };
};